<template>
  <div>
    <div class="sf-below-2">
      <div class="sf-subnav sf-subnav-fluid sf-below-1">
        <div class="sf-subnav-inner">
          <div
            :id="'tab-rt'"
            :class="[
              'sf-subnav-item',
              { active: templateType === 'reconciliation_text' },
            ]"
          >
            <a
              href="#"
              @click.prevent="switchTemplateType('reconciliation_text')"
            >
              <span>Reconciliation Texts</span>
            </a>
          </div>
          <div
            :id="'tab-adt'"
            :class="[
              'sf-subnav-item',
              { active: templateType === 'account_detail_template' },
            ]"
          >
            <a
              href="#"
              @click.prevent="switchTemplateType('account_detail_template')"
            >
              <span>Account Detail Templates</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <form :action="formAction" method="post" @submit.prevent="handleSubmit">
      <input
        type="hidden"
        name="authenticity_token"
        :value="authenticityToken"
      />
      <input
        type="hidden"
        name="update_locked_template_versions[template_type]"
        :value="templateType"
      />

      <div class="sf-below-2">
        <label class="tw-block tw-font-semibold">Template</label>
        <select
          v-model="selectedTemplateId"
          name="update_locked_template_versions[template_id]"
          @change="onTemplateChange"
        >
          <option value="">Select a template...</option>
          <option
            v-for="template in templates"
            :key="template.id"
            :value="template.id"
          >
            {{ template.name }}
          </option>
        </select>
      </div>

      <div id="companies-container" class="sf-below-2">
        <CompaniesTable
          v-if="companies.length > 0"
          :companies="companies"
          :selected-company-ids="selectedCompanyIds"
          @update:selected-company-ids="handleCompanySelectionChange"
        />
      </div>

      <div class="sf-below-2">
        <div id="selected-company-ids-container">
          <input
            v-for="id in selectedCompanyIds"
            :key="id"
            type="hidden"
            name="update_locked_template_versions[company_ids][]"
            :value="id"
          />
        </div>
        <div
          v-if="selectedCompanyIds.length > 0"
          id="update-summary"
          class="tw-mt-3 tw-text-sm tw-text-gray-600"
        >
          {{ totalPeriods }} locked ledgers will be updated
        </div>
      </div>

      <div class="sf-level sf-level-right">
        <button
          id="start-update-btn"
          type="submit"
          class="sf-btn sf-btn-success"
          :disabled="selectedCompanyIds.length === 0"
          :class="{ 'sf-disabled': selectedCompanyIds.length === 0 }"
        >
          Start Update
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import SFetch from '@app/utils/sfetch'
import type { SfConfirm } from '@app/types'
import CompaniesTable from './companies-table.vue'

interface TemplateData {
  id: number
  name: string
}

interface CompanyData {
  id: number
  name: string
  periodsCount: number
  currentVersion: number
  latestVersion: number
}

interface Props {
  firmId: string
  loadTemplatesUrl: string
  loadCompaniesUrl: string
  formAction: string
  authenticityToken: string
}

const props = defineProps<Props>()

const templateType = ref<string>('reconciliation_text')
const selectedTemplateId = ref<string>('')
const templates = ref<TemplateData[]>([])
const companies = ref<CompanyData[]>([])
const selectedCompanyIds = ref<number[]>([])
const isLoading = ref<boolean>(false)

const totalPeriods = computed(() => {
  return selectedCompanyIds.value.reduce((total, companyId) => {
    const company = companies.value.find((c) => c.id === companyId)
    return total + (company?.periodsCount || 0)
  }, 0)
})

const switchTemplateType = (type: string): void => {
  templateType.value = type
  selectedTemplateId.value = ''
  companies.value = []
  selectedCompanyIds.value = []
  void loadTemplates()
}

const loadTemplates = async (): Promise<void> => {
  isLoading.value = true
  try {
    const url = `${props.loadTemplatesUrl}?template_type=${templateType.value}`
    const response = (await SFetch.json(url)) as { templates: TemplateData[] }

    if (response?.templates) {
      templates.value = response.templates
    }
  } catch (error) {
    console.error('Failed to load templates:', error)
  } finally {
    isLoading.value = false
  }
}

const loadCompanies = async (): Promise<void> => {
  if (!selectedTemplateId.value) {
    companies.value = []
    selectedCompanyIds.value = []
    return
  }

  isLoading.value = true
  try {
    const url = `${props.loadCompaniesUrl}?template_type=${templateType.value}&template_id=${selectedTemplateId.value}`
    const response = (await SFetch.json(url)) as { companies: CompanyData[] }

    if (response?.companies) {
      companies.value = response.companies
      selectedCompanyIds.value = []
    }
  } catch (error) {
    console.error('Failed to load companies:', error)
  } finally {
    isLoading.value = false
  }
}

const onTemplateChange = (): void => {
  void loadCompanies()
}

const handleCompanySelectionChange = (ids: number[]): void => {
  selectedCompanyIds.value = ids
}

const submitFormFromEvent = (event: Event): void => {
  const form = event.target as HTMLFormElement
  if (form) {
    form.submit()
  }
}

const handleSubmit = (event: Event): void => {
  event.preventDefault()

  if (selectedCompanyIds.value.length === 0) {
    alert('Please select at least one company to update')
    return
  }

  const sfConfirm = window.sfConfirm as SfConfirm<() => void>
  if (sfConfirm) {
    sfConfirm({
      question:
        'I understand this action cannot be undone. Are you sure you want to start the bulk template version update?',
      confirmLabel: 'Start Update',
      cancelLabel: 'Cancel',
      onConfirm: () => {
        submitFormFromEvent(event)
      },
    })
  } else {
    if (
      confirm(
        'I understand this action cannot be undone. Are you sure you want to start the bulk template version update?'
      )
    ) {
      submitFormFromEvent(event)
    }
  }
}

onMounted(() => {
  void loadTemplates()
})
</script>
